/**
 * @fileoverview Eligible-reporting-managers query hook.
 * @description Wraps GET /users/eligible-reporting-managers?targetRoleId=… — who a new user with
 * the chosen Sales role may report to. Disabled until a Sales role is picked. Lives in shared,
 * under the shared per-role query key, so Add User, Invite User and Reactivate User read one
 * cached list per role (and the lifecycle cache helper refreshes them all) instead of each keeping
 * its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getEligibleReportingManagers } from '@/shared/api/directory-options.service'
import { eligibleReportingManagersQueryKey } from '@/shared/constants/directory-options-query-keys.constants'

import type { ReportingManagerOption } from '@/shared/types/directory-options.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the eligible reporting managers for a role.
 * @param {string} targetRoleId - The role being invited ('' while none is picked).
 * @param {boolean} enabled - Whether the role takes a reporting manager at all.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, ...).
 */
export function useEligibleReportingManagers(targetRoleId: string, enabled: boolean) {
  return useQuery<ReportingManagerOption[], ApiError>({
    queryKey: eligibleReportingManagersQueryKey(targetRoleId),
    queryFn: () => getEligibleReportingManagers(targetRoleId),
    enabled: enabled && targetRoleId.length > 0,
  })
}
